// Hand-drawn geometry: shapes, connectors and arrowheads rendered as sketchy SVG paths with rough.js.
import rough from "roughjs";
import type { Options } from "roughjs/bin/core";
import type { Dash, FillStyle, HeadKind, LineElement, ShapeElement, ShapeKind } from "../../shared/model";
import type { Vec } from "./geometry";

const gen = rough.generator();

export interface SketchPath {
  d: string;
  stroke: string;
  strokeWidth: number;
  fill: string;
  dash?: string;
  linecap?: "round";
}

class Lru<V> {
  private map = new Map<string, V>();
  constructor(private max: number) {}
  get(k: string): V | undefined {
    const v = this.map.get(k);
    if (v !== undefined) {
      this.map.delete(k);
      this.map.set(k, v);
    }
    return v;
  }
  set(k: string, v: V): void {
    this.map.set(k, v);
    if (this.map.size > this.max) this.map.delete(this.map.keys().next().value!);
  }
}

const shapeCache = new Lru<SketchPath[]>(1500);
const lineCache = new Lru<SketchPath[]>(1500);

function dashArray(dash: Dash, w: number): number[] | undefined {
  if (dash === "dashed") return [w * 4 + 4, w * 3 + 4];
  if (dash === "dotted") return [0.5, w * 2.5 + 3];
  return undefined;
}

function roughOptions(rough: number, seed: number, stroke: string, strokeWidth: number, dash: Dash): Options {
  const r = Math.max(0, Math.min(1, rough));
  return {
    seed: (seed % 2147483646) + 1,
    roughness: r * 2.6,
    bowing: r * 1.8,
    stroke,
    strokeWidth,
    disableMultiStroke: r < 0.12 || dash !== "solid",
    preserveVertices: r < 0.2,
    strokeLineDash: dashArray(dash, strokeWidth),
    curveStepCount: 12
  };
}

const RF: Record<FillStyle, Options["fillStyle"] | null> = {
  none: null,
  solid: "solid",
  hachure: "hachure",
  cross: "cross-hatch",
  dots: "dots"
};

function starPoints(w: number, h: number, n = 5): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < n * 2; i++) {
    const r = i % 2 === 0 ? 0.5 : 0.21;
    const a = -Math.PI / 2 + (i * Math.PI) / n;
    pts.push([w / 2 + Math.cos(a) * r * w, h * 0.55 + Math.sin(a) * r * h * 1.1]);
  }
  return pts;
}

/** Plain (not rough) outline for each shape kind, as SVG path data in a w×h box. */
export function shapeOutlinePath(kind: ShapeKind, w: number, h: number): string {
  const r = Math.min(w, h);
  switch (kind) {
    case "rect":
      return `M0,0H${w}V${h}H0Z`;
    case "roundrect": {
      const k = Math.min(r * 0.22, 36);
      return `M${k},0H${w - k}Q${w},0 ${w},${k}V${h - k}Q${w},${h} ${w - k},${h}H${k}Q0,${h} 0,${h - k}V${k}Q0,0 ${k},0Z`;
    }
    case "ellipse":
      return `M0,${h / 2}A${w / 2},${h / 2} 0 1,0 ${w},${h / 2}A${w / 2},${h / 2} 0 1,0 0,${h / 2}Z`;
    case "diamond":
      return `M${w / 2},0L${w},${h / 2}L${w / 2},${h}L0,${h / 2}Z`;
    case "triangle":
      return `M${w / 2},0L${w},${h}L0,${h}Z`;
    case "hexagon": {
      const k = w * 0.25;
      return `M${k},0L${w - k},0L${w},${h / 2}L${w - k},${h}L${k},${h}L0,${h / 2}Z`;
    }
    case "parallelogram": {
      const k = w * 0.2;
      return `M${k},0L${w},0L${w - k},${h}L0,${h}Z`;
    }
    case "star":
      return "M" + starPoints(w, h).map((p) => p.join(",")).join("L") + "Z";
    case "cloud":
      return (
        `M${w * 0.25},${h * 0.85}` +
        `C${w * 0.02},${h * 0.86} ${w * 0.0},${h * 0.52} ${w * 0.2},${h * 0.46}` +
        `C${w * 0.14},${h * 0.18} ${w * 0.44},${h * 0.04} ${w * 0.56},${h * 0.24}` +
        `C${w * 0.68},${h * 0.02} ${w * 0.94},${h * 0.16} ${w * 0.86},${h * 0.44}` +
        `C${w * 1.02},${h * 0.52} ${w * 0.98},${h * 0.88} ${w * 0.76},${h * 0.85}Z`
      );
    case "cylinder": {
      const e = Math.min(h * 0.14, w * 0.3);
      return `M0,${e}A${w / 2},${e} 0 1,0 ${w},${e}A${w / 2},${e} 0 1,0 0,${e}V${h - e}A${w / 2},${e} 0 0,0 ${w},${h - e}V${e}`;
    }
    case "heart":
      return (
        `M${w / 2},${h * 0.95}` +
        `C${w * 0.1},${h * 0.66} ${-w * 0.02},${h * 0.34} ${w * 0.16},${h * 0.14}` +
        `C${w * 0.32},${-h * 0.02} ${w * 0.5},${h * 0.1} ${w / 2},${h * 0.26}` +
        `C${w / 2},${h * 0.1} ${w * 0.68},${-h * 0.02} ${w * 0.84},${h * 0.14}` +
        `C${w * 1.02},${h * 0.34} ${w * 0.9},${h * 0.66} ${w / 2},${h * 0.95}Z`
      );
    case "speech": {
      const k = Math.min(r * 0.2, 28);
      const bh = h * 0.78;
      return `M${k},0H${w - k}Q${w},0 ${w},${k}V${bh - k}Q${w},${bh} ${w - k},${bh}H${w * 0.42}L${w * 0.2},${h}L${w * 0.26},${bh}H${k}Q0,${bh} 0,${bh - k}V${k}Q0,0 ${k},0Z`;
    }
  }
}

export function sketchShape(el: Pick<ShapeElement, "kind" | "w" | "h" | "stroke" | "fill" | "fillStyle" | "strokeWidth" | "dash" | "rough" | "seed">): SketchPath[] {
  const w = Math.max(2, Math.round(el.w * 2) / 2);
  const h = Math.max(2, Math.round(el.h * 2) / 2);
  const key = [el.kind, w, h, el.stroke, el.fill, el.fillStyle, el.strokeWidth, el.dash, el.rough, el.seed].join("|");
  const hit = shapeCache.get(key);
  if (hit) return hit;
  const o = roughOptions(el.rough, el.seed, el.stroke, el.strokeWidth, el.dash);
  const fillStyle = RF[el.fillStyle];
  if (fillStyle && el.fill && el.fill !== "transparent") {
    o.fill = el.fill;
    o.fillStyle = fillStyle;
    o.fillWeight = Math.max(1, el.strokeWidth * 0.55);
    o.hachureGap = Math.max(4, el.strokeWidth * 3.2 + 3);
    o.hachureAngle = -41;
  }
  let drawable;
  switch (el.kind) {
    case "rect":
      drawable = gen.rectangle(0, 0, w, h, o);
      break;
    case "ellipse":
      drawable = gen.ellipse(w / 2, h / 2, w, h, o);
      break;
    case "diamond":
      drawable = gen.polygon([[w / 2, 0], [w, h / 2], [w / 2, h], [0, h / 2]], o);
      break;
    case "triangle":
      drawable = gen.polygon([[w / 2, 0], [w, h], [0, h]], o);
      break;
    case "star":
      drawable = gen.polygon(starPoints(w, h), o);
      break;
    default:
      drawable = gen.path(shapeOutlinePath(el.kind, w, h), o);
  }
  const out: SketchPath[] = gen.toPaths(drawable).map((p) => ({
    d: p.d,
    stroke: p.stroke,
    strokeWidth: p.strokeWidth,
    fill: p.fill || "none",
    dash: o.strokeLineDash && p.stroke !== "none" && !p.fill ? o.strokeLineDash.join(" ") : undefined,
    linecap: "round"
  }));
  shapeCache.set(key, out);
  return out;
}

// ---- connectors ----

function headPaths(tip: Vec, from: Vec, kind: HeadKind, w: number, o: Options): SketchPath[] {
  if (kind === "none") return [];
  const ang = Math.atan2(tip.y - from.y, tip.x - from.x);
  const size = Math.max(10, w * 4 + 6);
  const at = (a: number, r: number): [number, number] => [tip.x + Math.cos(a) * r, tip.y + Math.sin(a) * r];
  const ho: Options = { ...o, strokeLineDash: undefined, disableMultiStroke: true, roughness: (o.roughness ?? 0) * 0.5 };
  let dr;
  switch (kind) {
    case "arrow":
      dr = gen.linearPath([at(ang + Math.PI - 0.45, size), [tip.x, tip.y], at(ang + Math.PI + 0.45, size)], ho);
      break;
    case "triangle":
      dr = gen.polygon([[tip.x, tip.y], at(ang + Math.PI - 0.4, size), at(ang + Math.PI + 0.4, size)], {
        ...ho, fill: o.stroke, fillStyle: "solid"
      });
      break;
    case "dot":
      dr = gen.circle(tip.x - Math.cos(ang) * size * 0.3, tip.y - Math.sin(ang) * size * 0.3, size * 0.55, { ...ho, fill: o.stroke, fillStyle: "solid" });
      break;
    case "bar":
      dr = gen.line(...at(ang + Math.PI / 2, size * 0.5), ...at(ang - Math.PI / 2, size * 0.5), ho);
      break;
    case "diamond": {
      const back = at(ang + Math.PI, size * 1.1);
      dr = gen.polygon([[tip.x, tip.y], at(ang + Math.PI - 0.5, size * 0.6), back, at(ang + Math.PI + 0.5, size * 0.6)], {
        ...ho, fill: o.stroke, fillStyle: "solid"
      });
      break;
    }
  }
  return gen.toPaths(dr).map((p) => ({ d: p.d, stroke: p.stroke, strokeWidth: p.strokeWidth, fill: p.fill || "none", linecap: "round" as const }));
}

/** World-space polyline of a connector (after routing). */
export function routePoints(el: Pick<LineElement, "x" | "y" | "pts" | "route">): Vec[] {
  const pts = el.pts.map(([px, py]) => ({ x: el.x + px, y: el.y + py }));
  if (el.route !== "elbow" || pts.length < 2) return pts;
  const a = pts[0];
  const b = pts[pts.length - 1];
  const dx = Math.abs(b.x - a.x);
  const dy = Math.abs(b.y - a.y);
  if (dx >= dy) {
    const mx = (a.x + b.x) / 2;
    return [a, { x: mx, y: a.y }, { x: mx, y: b.y }, b];
  }
  const my = (a.y + b.y) / 2;
  return [a, { x: a.x, y: my }, { x: b.x, y: my }, b];
}

/** Points along a curved connector (Catmull-Rom through its points), used for hit tests and labels. */
export function curvePoints(pts: Vec[], steps = 16): Vec[] {
  if (pts.length < 3) return pts;
  const out: Vec[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      const t2 = t * t;
      const t3 = t2 * t;
      out.push({
        x: 0.5 * (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
        y: 0.5 * (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3)
      });
    }
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/** The drawn path of a connector in world coordinates. */
export function linePolyline(el: LineElement): Vec[] {
  const pts = routePoints(el);
  return el.route === "curve" ? curvePoints(pts) : pts;
}

export function sketchLine(el: LineElement): SketchPath[] {
  const poly = linePolyline(el);
  const key = [
    poly.map((p) => `${Math.round(p.x * 2) / 2},${Math.round(p.y * 2) / 2}`).join(";"),
    el.route, el.stroke, el.strokeWidth, el.dash, el.rough, el.seed, el.start.head, el.end.head
  ].join("|");
  const hit = lineCache.get(key);
  if (hit) return hit;
  const o = roughOptions(el.rough, el.seed, el.stroke, el.strokeWidth, el.dash);
  const raw = routePoints(el);
  const pairs = raw.map((p) => [p.x, p.y] as [number, number]);
  const body = el.route === "curve" && pairs.length > 2 ? gen.curve(pairs, o) : gen.linearPath(pairs, o);
  const out: SketchPath[] = gen.toPaths(body).map((p) => ({
    d: p.d, stroke: p.stroke, strokeWidth: p.strokeWidth, fill: "none",
    dash: o.strokeLineDash ? o.strokeLineDash.join(" ") : undefined, linecap: "round"
  }));
  if (poly.length >= 2) {
    const end = poly[poly.length - 1];
    const beforeEnd = poly[Math.max(0, poly.length - 3)];
    out.push(...headPaths(end, beforeEnd, el.end.head, el.strokeWidth, o));
    const start = poly[0];
    const afterStart = poly[Math.min(poly.length - 1, 2)];
    out.push(...headPaths(start, afterStart, el.start.head, el.strokeWidth, o));
  }
  lineCache.set(key, out);
  return out;
}
